/**
 * "The network is busy with something a child is about to need."
 *
 * 🔴 ON A FIRST VISIT THE OFFLINE CACHE AND THE NEXT GAME COMPETE FOR ONE LINK.
 * Registering the service worker starts its precache — ~60 MB — at full speed,
 * and on a 10 Mbps classroom link that is the whole link for a minute. Measured:
 * a teacher spent 30 s on the play dialog while Kalqy World prefetched, and the
 * game still took 9.9 s to start, because the precache had the bandwidth.
 *
 * So the things a child is waiting on — the game a teacher just picked, the
 * camera's files, a game's own loading card — mark the network busy, and the
 * service worker registers once nothing is (`whenNetworkQuiet`). The offline
 * cache still fills during the first session, just behind the round instead of
 * in front of it.
 */

let busy = 0
const waiters = new Set<() => void>()

/** Mark the network busy until the returned function is called. Safe to call the release twice. */
export function holdNetwork(): () => void {
  busy += 1
  let released = false
  return () => {
    if (released) return
    released = true
    busy -= 1
    if (busy === 0) for (const wake of [...waiters]) wake()
  }
}

/** Track a promise as network work; it is released however the promise settles. */
export function whileBusy<T>(work: Promise<T>): Promise<T> {
  const release = holdNetwork()
  return work.finally(release)
}

/**
 * Resolves when nothing has held the network for `settleMs` — or after `maxMs`
 * regardless, so a page that is never quiet still gets its offline cache.
 */
export function whenNetworkQuiet({ settleMs = 2000, maxMs = 60_000 }: { settleMs?: number; maxMs?: number } = {}): Promise<void> {
  return new Promise((resolve) => {
    let settle = 0
    const done = (): void => {
      waiters.delete(check)
      clearTimeout(settle)
      clearTimeout(cap)
      resolve()
    }
    const check = (): void => {
      clearTimeout(settle)
      if (busy === 0) settle = setTimeout(() => (busy === 0 ? done() : undefined), settleMs) as unknown as number
    }
    const cap = setTimeout(done, maxMs) as unknown as number
    waiters.add(check)
    check()
  })
}

/** Test seam. */
export function __resetNetworkPriorityForTests(): void {
  busy = 0
  waiters.clear()
}
